import { OfferViewT } from "../../../../../offers/domain";
import { SortByList } from "../../../../../../shared/domain";
import toast from "react-hot-toast";
import {
  ArrowIcon,
  Button,
  CaretIcon,
  Form,
  MenuValue,
  SelectMenu,
} from "../../../../../../shared/infrastructure/ui";
import { Controller, useFormContext } from "react-hook-form";
import {
  DefaultViewFormSchema,
  UpdateUserViewSettingsPayload,
  UserDefaultViewOptions,
} from "../../../../domain";
import { OrderTypes, SortByT } from "../../../../../../shared/domain/entities";
import React, { useMemo } from "react";
import {
  useGetUserInfo,
  useUpdateUserInfo,
} from "../../../../../../shared/infrastructure/services/user.services";

type DefaultViewOptionsProps = {
  onClose: () => void;
};

const DefaultViewForm: React.FC = () => {
  const {
    control,
    formState: { defaultValues },
  } = useFormContext<UpdateUserViewSettingsPayload>();

  return (
    <div className="grid gap-5 p-6">
      <div className="grid gap-3">
        <h1 className="text-xl">Default View Settings</h1>
        <p className="text-sm">
          Set some preferences for the default view in the offers lists
        </p>
      </div>
      <div className="grid grid-cols-2">
        <div>
          <p className="mb-1 text-sm font-bold">Default view</p>
          <SelectMenu
            name="default_lists_view"
            defaultValue={defaultValues?.default_lists_view as MenuValue}
            items={UserDefaultViewOptions}
            renderItem={({ label }) => (
              <section className="grid grid-flow-col items-center gap-[10px] text-[12px] capitalize">
                {label}
              </section>
            )}
          >
            {({ label }) => (
              <div className="flex w-fit items-center rounded-[5px] border border-black bg-[#FFFFFF]">
                <Button
                  className="w-28 border-none hover:bg-transparent"
                  endIcon={<CaretIcon />}
                  appearance="secondary"
                  classes={{ label: "flex justify-between" }}
                  type="button"
                >
                  {label}
                </Button>
              </div>
            )}
          </SelectMenu>
        </div>
        <div>
          <p className="mb-1 text-sm font-bold">Queue list order</p>
          <div className="ml-5 flex w-fit items-center rounded-[5px] border border-black bg-[#FFFFFF] md:ml-0">
            <Controller
              control={control}
              name="default_queue_list_order_by"
              render={({ field: { value, onChange } }) => (
                <Button
                  className="w-10 min-w-0 border-0 border-transparent bg-transparent hover:bg-transparent"
                  appearance="secondary"
                  onClick={() =>
                    onChange(
                      value === OrderTypes.DESC
                        ? OrderTypes.ASC
                        : OrderTypes.DESC
                    )
                  }
                  type="button"
                >
                  <ArrowIcon
                    className={value !== OrderTypes.DESC ? "rotate-180" : ""}
                  />
                </Button>
              )}
            />
            <SelectMenu
              name="default_queue_list_sort_by"
              defaultValue={
                defaultValues?.default_queue_list_sort_by as MenuValue
              }
              items={SortByList}
              renderItem={({ label }) => (
                <div className="grid grid-flow-col items-center gap-[10px] text-[12px] capitalize">
                  {label}
                </div>
              )}
            >
              {({ label }) => (
                <Button
                  className="w-28 border-0 border-transparent bg-transparent hover:bg-transparent"
                  endIcon={<CaretIcon />}
                  appearance="secondary"
                  classes={{ label: "flex justify-between" }}
                  type="button"
                >
                  {label}
                </Button>
              )}
            </SelectMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

export const DefaultViewOptions: React.FC<DefaultViewOptionsProps> = ({
  onClose,
}) => {
  const { data: user } = useGetUserInfo();
  const { mutateAsync: updateUser, isLoading } = useUpdateUserInfo();

  const defaultFormValues: UpdateUserViewSettingsPayload = useMemo(
    () => ({
      default_queue_list_order_by:
        user?.default_queue_list_order_by ?? OrderTypes.DESC,
      default_queue_list_sort_by:
        user?.default_queue_list_sort_by ?? (SortByList[0].value as SortByT),
      default_lists_view:
        user?.default_lists_view ??
        (UserDefaultViewOptions[0].value as OfferViewT),
    }),
    [user]
  );

  const onSubmitForm = (values: UpdateUserViewSettingsPayload): void => {
    void updateUser(values)
      .then(() => {
        toast.success("Single offer has been updated");
        onClose();
      })
      .catch(() => {
        toast.error("Something happen, please try again later");
      });
  };

  return (
    <Form
      onSubmit={onSubmitForm}
      schema={DefaultViewFormSchema}
      mode="onChange"
      defaultValues={defaultFormValues}
    >
      <section className="w-[450px]">
        <DefaultViewForm />
        <div className="m-6 flex justify-end">
          <Button
            className="w-auto bg-black px-[16px] py-[6px] text-[12px]"
            status={isLoading ? "loading" : "default"}
            type="submit"
          >
            Save
          </Button>
        </div>
      </section>
    </Form>
  );
};
